import React, { useContext, useState } from 'react';
import { AppContext } from '../../App';
import { renameCurrentLab } from '../../clients/lab_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { isLabAdmin } from '../../utils/userAccountRole';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import * as formik from 'formik';
import * as yup from 'yup';

const LOG_CONTEXT = 'LabNameForm';

const labNameSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required('Lab name is required')
    .max(255, 'Lab name must be at most 255 characters'),
});

/**
 * Admin-only form to rename the current lab display name (Lab settings page).
 *
 * @param {Object} props
 * @param {string} props.initialName - Current lab name from AppContext.
 */
const LabNameForm = ({ initialName = '' }) => {
  const { token, userDetails, isSupplier, setCurrentLab } = useContext(AppContext);
  const labAdmin = isLabAdmin(userDetails, isSupplier);
  const [editDisabled, setEditDisabled] = useState(false);
  const { Formik } = formik;

  if (!labAdmin || editDisabled) {
    return null;
  }

  return (
    <Formik
      initialValues={{ name: initialName }}
      enableReinitialize
      validationSchema={labNameSchema}
      onSubmit={async (values, { setSubmitting, setFieldError }) => {
        setSubmitting(true);
        logInfo(LOG_CONTEXT, 'rename lab submit', { nameLength: values.name.trim().length });

        const result = await renameCurrentLab(token, values.name);
        setSubmitting(false);

        if (result.success) {
          setCurrentLab(result.data);
          showToast('Lab name updated.', 'success', 'top-right', 3000);
          logInfo(LOG_CONTEXT, 'rename lab success', { labId: result.data?.id });
          return;
        }

        if (result.status === 403) {
          setEditDisabled(true);
          showToast('Only lab Admins can rename the lab.', 'error', 'top-right', 4000);
          return;
        }

        if (result.status === 400 && result.messages?.length) {
          result.messages.forEach(msg => {
            if (msg.toLowerCase().includes('name')) {
              setFieldError('name', msg.replace(/^name:\s*/i, ''));
            }
          });
          return;
        }

        logError(LOG_CONTEXT, 'rename lab failed', {
          status: result.status ?? null,
          messages: result.messages,
        });
        showToast('Failed to update lab name.', 'error', 'top-right', 4000);
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, dirty }) => (
        <Form onSubmit={handleSubmit} className="lab-name-form">
          <Form.Group controlId="labName" className="mb-2">
            <Form.Label className="account-page__label">Lab name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={255}
              isInvalid={touched.name && Boolean(errors.name)}
              disabled={isSubmitting}
            />
            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            <Form.Text muted>{values.name.length}/255</Form.Text>
          </Form.Group>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSubmitting || !dirty}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving…
              </>
            ) : (
              'Save lab name'
            )}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default LabNameForm;
